import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, FileDown, Presentation, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import {
  buildOffrePptxFilename,
  computeSalaireBrutByStaffLine,
  FRAIS_GENERALES_CATEGORIES,
  parseNum,
} from './devisQuoteState';

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0
  );

const fgLineAmount = (line) => parseNum(line.amountHt, 0);

const DevisQuoteResumeTab = ({
  quoteId,
  quoteTitle = '',
  quoteDate = '',
  clientCompany = '',
  token,
  apiBase,
  salaires,
  qualifications,
  mainOeuvre,
  operationCategories,
  fraisGeneraux,
  investissement,
  theme,
  quoteFinalized = false,
  canChangeStatus = true,
  statusActionLoading = false,
  onFinalizeQuote,
  onReopenDraft,
}) => {
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const linesByCategoryFg = fraisGeneraux?.linesByCategory || {};
  const linesByCategoryInv = investissement?.linesByCategory || {};
  const anneesStr = investissement?.annees_amortissement ?? '3';

  const staffBrutRows = useMemo(
    () =>
      computeSalaireBrutByStaffLine(salaires, qualifications, mainOeuvre, operationCategories),
    [salaires, qualifications, mainOeuvre, operationCategories]
  );

  const salaireBrutAnnuel = useMemo(
    () => staffBrutRows.reduce((s, r) => s + r.brutAnnuel, 0),
    [staffBrutRows]
  );

  const prime = parseNum(salaires.primeObjectif, 400);
  const pctCharges = parseNum(salaires.chargesSocialesPct, 32);
  const totalChargesSociales = (salaireBrutAnnuel + prime) * (pctCharges / 100);

  const tr = parseNum(salaires.titreRestaurant, 0);
  const transp = parseNum(salaires.transport, 0);
  const totalAvantagesTrTransport = tr + transp;

  const { totalPrestataireHt, amortAnnuelHt, nAnneesAmort } = useMemo(() => {
    let tp = 0;
    for (const lines of Object.values(linesByCategoryInv)) {
      if (!Array.isArray(lines)) continue;
      for (const l of lines) {
        tp += parseNum(l.prestataire_ht, 0);
      }
    }
    const nAnnees = parseNum(anneesStr, 0);
    const amort = nAnnees > 0 ? tp / nAnnees : 0;
    return { totalPrestataireHt: tp, amortAnnuelHt: amort, nAnneesAmort: nAnnees };
  }, [linesByCategoryInv, anneesStr]);

  /** Charge annuelle HT : total investissement prestataire ÷ nombre d’années d’amortissement. */
  const totalInvestissementHt = amortAnnuelHt;

  const categoryTotalsFg = useMemo(() => {
    const out = {};
    for (const cat of FRAIS_GENERALES_CATEGORIES) {
      const lines = linesByCategoryFg[cat.id] || [];
      out[cat.id] = lines.reduce((s, l) => s + fgLineAmount(l), 0);
    }
    return out;
  }, [linesByCategoryFg]);

  const totalFraisGenerauxHt = useMemo(
    () => Object.values(categoryTotalsFg).reduce((a, b) => a + b, 0),
    [categoryTotalsFg]
  );

  const totalPersonnelAnnuelHt =
    salaireBrutAnnuel + totalChargesSociales + totalAvantagesTrTransport;

  const totalAnnuelFraisFixesHt =
    totalPersonnelAnnuelHt + amortAnnuelHt + totalFraisGenerauxHt;

  const downloadExport = async (format) => {
    if (!quoteId || !token || !apiBase) {
      toast.error(`Export ${format === 'pdf' ? 'PDF' : 'PowerPoint'} indisponible.`);
      return;
    }
    const isPdf = format === 'pdf';
    const setLoading = isPdf ? setExportingPdf : setExportingPptx;
    setLoading(true);
    try {
      const ext = isPdf ? 'pdf' : 'pptx';
      const url = `${apiBase}/quotes/${encodeURIComponent(quoteId)}/export.${ext}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const text = await res.text();
        let msg = text || res.statusText;
        try {
          const j = JSON.parse(text);
          if (j?.detail) msg = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
        } catch {
          if (text) msg = text.slice(0, 240);
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const fallbackName = buildOffrePptxFilename({
        clientCompany,
        quoteTitle,
        quoteDate,
        quoteId,
      }).replace(/\.pptx$/i, `.${ext}`);
      const cd = res.headers.get('Content-Disposition') || '';
      let filename = null;
      const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const plain = cd.match(/filename="([^"]+)"/i);
      if (star?.[1]) {
        try {
          filename = decodeURIComponent(star[1].trim());
        } catch {
          filename = star[1];
        }
      } else if (plain?.[1]) {
        filename = plain[1];
      }
      const lower = String(filename || '').toLowerCase();
      if (!filename || !lower.endsWith(`.${ext}`)) {
        filename = fallbackName;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(isPdf ? 'PDF généré.' : 'PowerPoint généré.');
    } catch (e) {
      toast.error(e?.message || `Échec de l’export ${isPdf ? 'PDF' : 'PowerPoint'}.`);
    } finally {
      setLoading(false);
    }
  };

  const panelStyle = { borderColor: theme.border, backgroundColor: theme.surface };
  const insetStyle = { borderColor: theme.border, backgroundColor: theme.panelInset };

  return (
    <div className="space-y-5 min-w-0 sm:space-y-6">
      <div
        className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 px-4 py-4 shadow-sm sm:px-5 sm:py-5"
        style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Total annuel (frais fixes)
          </p>
          <p className="text-xs mt-1.5 leading-relaxed sm:text-sm" style={{ color: theme.textSecondary }}>
            Personnel (brut + charges + avantages) + amortissement annuel invest. + frais généraux
          </p>
        </div>
        <p className="text-2xl font-bold tabular-nums shrink-0 sm:text-3xl" style={{ color: theme.text }}>
          {fmtEur(totalAnnuelFraisFixesHt)} HT
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-stretch sm:justify-between sm:gap-4">
        <p className="text-sm min-w-0 flex-1 leading-relaxed sm:text-base" style={{ color: theme.textSecondary }}>
          Synthèse des montants annuels (€ HT). Export PowerPoint (trame{' '}
          <span className="font-medium" style={{ color: theme.text }}>
            docs/chiffrage/EXEMPLE SUPPORT.pptx
          </span>
          {' '}si présente) ou PDF (sections renseignées uniquement).
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            className="h-12 min-h-[48px] w-full gap-2 rounded-xl px-4 font-semibold sm:h-11 sm:w-auto touch-manipulation"
            disabled={exportingPptx || exportingPdf}
            onClick={() => downloadExport('pptx')}
          >
            <Presentation className="h-5 w-5 shrink-0" />
            {exportingPptx ? 'Génération…' : 'Télécharger le devis (PowerPoint)'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-12 min-h-[48px] w-full gap-2 rounded-xl px-4 font-semibold sm:h-11 sm:w-auto touch-manipulation"
            disabled={exportingPptx || exportingPdf}
            onClick={() => downloadExport('pdf')}
          >
            <FileDown className="h-5 w-5 shrink-0" />
            {exportingPdf ? 'Génération…' : 'Télécharger le devis (PDF)'}
          </Button>
          {canChangeStatus && !quoteFinalized && (
            <Button
              type="button"
              className="h-12 min-h-[48px] w-full gap-2 rounded-xl px-4 font-semibold shadow-sm sm:h-11 sm:w-auto touch-manipulation"
              style={{ backgroundColor: theme.accent, color: '#fff' }}
              disabled={statusActionLoading}
              onClick={() => onFinalizeQuote?.()}
            >
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              {statusActionLoading ? 'En cours…' : 'Finaliser le devis'}
            </Button>
          )}
          {canChangeStatus && quoteFinalized && (
            <Button
              type="button"
              variant="outline"
              className="h-12 min-h-[48px] w-full gap-2 rounded-xl border-2 px-4 font-semibold sm:h-11 sm:w-auto touch-manipulation hover:brightness-105 active:brightness-95 disabled:opacity-60"
              style={
                theme.background === '#0f1419'
                  ? {
                      borderColor: '#22d3ee',
                      color: '#ecfeff',
                      backgroundColor: 'rgba(34, 211, 238, 0.22)',
                      boxShadow:
                        '0 0 0 1px rgba(34, 211, 238, 0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
                    }
                  : {
                      borderColor: theme.accent,
                      color: theme.text,
                      backgroundColor: theme.accentSoft,
                    }
              }
              disabled={statusActionLoading}
              onClick={() => onReopenDraft?.()}
            >
              <RotateCcw
                className="h-5 w-5 shrink-0"
                style={theme.background === '#0f1419' ? { color: '#a5f3fc' } : undefined}
              />
              {statusActionLoading ? 'En cours…' : 'Revenir en brouillon'}
            </Button>
          )}
        </div>
      </div>

      <section
        className="rounded-2xl border-2 p-4 shadow-sm space-y-5 sm:p-5"
        style={panelStyle}
      >
        <h2 className="text-base font-bold sm:text-lg" style={{ color: theme.text }}>
          Détails des frais fixes annuels
        </h2>

        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Frais de personnel chargés
          </h3>
          <div className="-mx-1 overflow-x-auto rounded-xl border-2 sm:mx-0" style={insetStyle}>
            <table className="w-full min-w-[280px] text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: theme.border }}>
                  <th className="text-left font-semibold px-3 py-3 sm:px-4" style={{ color: theme.textSecondary }}>
                    Poste
                  </th>
                  <th className="text-left font-semibold px-3 py-3 hidden sm:table-cell sm:px-4" style={{ color: theme.textSecondary }}>
                    Type d’horaire
                  </th>
                  <th className="text-right font-semibold px-3 py-3 tabular-nums sm:px-4" style={{ color: theme.textSecondary }}>
                    Montant HT (brut annuel)
                  </th>
                </tr>
              </thead>
              <tbody>
                {staffBrutRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-3 text-xs" style={{ color: theme.textSecondary }}>
                      Aucune ligne d’affectation.
                    </td>
                  </tr>
                ) : (
                  staffBrutRows.map((row) => (
                    <tr key={row.lineId} className="border-b last:border-0" style={{ borderColor: theme.border }}>
                      <td className="px-3 py-3 align-top sm:px-4" style={{ color: theme.text }}>
                        <span className="font-medium">{row.postLabel}</span>
                        <span className="sm:hidden block text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                          {row.hourTypeLabel}
                        </span>
                      </td>
                      <td className="px-3 py-3 hidden sm:table-cell align-top sm:px-4" style={{ color: theme.textSecondary }}>
                        {row.hourTypeLabel}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold sm:px-4" style={{ color: theme.text }}>
                        {fmtEur(row.brutAnnuel)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border-2 px-4 py-3" style={insetStyle}>
              <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Total personnel — Charges sociales annuelles
              </p>
              <p className="text-base font-semibold tabular-nums mt-1" style={{ color: theme.text }}>
                {fmtEur(totalChargesSociales)} HT
              </p>
            </div>
            <div className="rounded-xl border-2 px-4 py-3" style={insetStyle}>
              <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Total personnel — Avantages, TR et transport
              </p>
              <p className="text-base font-semibold tabular-nums mt-1" style={{ color: theme.text }}>
                {fmtEur(totalAvantagesTrTransport)} HT
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2 border-t pt-4" style={{ borderColor: theme.border }}>
          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Investissement
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border-2 px-4 py-3" style={insetStyle}>
            <div className="min-w-0">
              <span className="text-sm font-medium block" style={{ color: theme.text }}>
                Total investissement
              </span>
              <span className="text-[11px] mt-0.5 block leading-snug" style={{ color: theme.textSecondary }}>
                Total prestataire (€ HT) ÷ nombre d’années d’amortissement — charge annuelle
              </span>
            </div>
            <span className="text-base font-bold tabular-nums shrink-0" style={{ color: theme.text }}>
              {fmtEur(totalInvestissementHt)} HT
            </span>
          </div>
          {totalPrestataireHt > 0 && (
            <p className="text-xs" style={{ color: theme.textSecondary }}>
              Total investissements prestataire (hors amortissement) :{' '}
              <span className="font-medium tabular-nums" style={{ color: theme.text }}>
                {fmtEur(totalPrestataireHt)} HT
              </span>
              {nAnneesAmort > 0 ? (
                <>
                  {' '}
                  · {nAnneesAmort} an{nAnneesAmort > 1 ? 's' : ''} d’amortissement
                </>
              ) : (
                <span> — indiquez une durée d’amortissement dans l’onglet Investissement pour calculer la charge annuelle.</span>
              )}
            </p>
          )}
        </div>

        <div className="space-y-2 border-t pt-4" style={{ borderColor: theme.border }}>
          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Frais généraux
          </h3>
          <ul className="rounded-xl border-2 overflow-hidden" style={{ borderColor: theme.border }}>
            {FRAIS_GENERALES_CATEGORIES.map((cat) => (
              <li
                key={cat.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-3 text-sm last:border-b-0 sm:px-4"
                style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
              >
                <span className="min-w-0" style={{ color: theme.text }}>
                  {cat.label}
                </span>
                <span className="tabular-nums font-medium shrink-0" style={{ color: theme.text }}>
                  {fmtEur(categoryTotalsFg[cat.id] ?? 0)} HT
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border-2 px-4 py-3" style={insetStyle}>
            <span className="text-sm font-semibold" style={{ color: theme.text }}>
              Total frais généraux
            </span>
            <span className="text-base font-bold tabular-nums shrink-0" style={{ color: theme.text }}>
              {fmtEur(totalFraisGenerauxHt)} HT
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevisQuoteResumeTab;
